import type { CSSProperties, FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'

import { ImagePlate } from '@/components/generals/image-plate'
import { WordReveal } from '@/components/generals/word-reveal'
import { useParallax } from '@/hooks/use-parallax'

import { EASE, MICRO_FAINT } from '@/constants/type'
import { studio } from '@/constants/site'

export const AboutHero: FC = (): ReactNode => {
    const [ready, setReady] = useState(false)
    const drift = useParallax<HTMLDivElement>(-46)

    useEffect(() => {
        setReady(true)
    }, [])

    const rise = (delay: number): CSSProperties => ({
        opacity: ready ? 1 : 0,
        transform: ready ? 'translateY(0)' : 'translateY(10px)',
        transition: `opacity 0.7s ${EASE} ${delay}s, transform 0.7s ${EASE} ${delay}s`
    })

    return (
        <header
            id='about-hero'
            className='flex min-h-[82svh] flex-col px-6 pb-12 pt-28 sm:px-12 sm:pb-14 sm:pt-40'
        >
            <div className='grid grid-cols-12 items-baseline gap-x-6'>
                <p className={`col-span-6 sm:col-span-2 ${MICRO_FAINT}`} style={rise(0.05)}>
                    About
                </p>

                <p
                    className={`col-span-6 text-right sm:col-start-11 sm:col-span-2 sm:text-left ${MICRO_FAINT}`}
                    style={rise(0.14)}
                >
                    Est. {studio.founded}
                </p>
            </div>

            <div className='grid flex-1 grid-cols-12 items-stretch gap-x-6 gap-y-10 pt-10 sm:pt-12'>
                {/* The standfirst holds the top of the column so the headline can
                    sit on the baseline without leaving a void above it. */}
                <div className='col-span-12 flex flex-col justify-between gap-10 lg:col-span-7'>
                    <p
                        className='max-w-[34ch] text-[15px] leading-relaxed text-fz-sub'
                        style={rise(0.24)}
                    >
                        Frieze is a design studio working in identity, typeface design, editorial,
                        packaging and wayfinding. We photograph most of it ourselves.
                    </p>

                    <div ref={drift}>
                        <WordReveal
                            as='h1'
                            text='We make things that reward a second look.'
                            delay={0.3}
                            stagger={0.06}
                            className='max-w-[15ch] font-serif tracking-[-0.03em] text-fz-ink'
                            style={{ fontSize: 'clamp(2.5rem, 7.2vw, 5.75rem)', lineHeight: 0.96 }}
                        />
                    </div>
                </div>

                <div className='col-span-12 flex items-end lg:col-start-8 lg:col-span-5'>
                    <div className='grid w-full grid-cols-5 items-end gap-x-4 sm:gap-x-6'>
                        <ImagePlate
                            className='col-span-2'
                            src='/images/about-hero-a.jpg'
                            alt='Dusk portrait, headlights behind'
                            ratio='3 / 4'
                            focus='60% 30%'
                            trigger='mount'
                            priority
                            delay={0.55}
                            drift={26}
                        />

                        <ImagePlate
                            className='col-span-3'
                            src='/images/about-hero-b.jpg'
                            alt='Portrait held in low evening light'
                            ratio='4 / 5'
                            focus='50% 34%'
                            trigger='mount'
                            priority
                            delay={0.72}
                            drift={-32}
                            caption='Marlow — plate 04'
                        />
                    </div>
                </div>
            </div>

            <div className='mt-12 sm:mt-14'>
                <span
                    aria-hidden='true'
                    className='block h-px w-full origin-left bg-fz-rule'
                    style={{
                        transform: ready ? 'scaleX(1)' : 'scaleX(0)',
                        transition: `transform 1.3s ${EASE} 0.6s`
                    }}
                />

                <div className='mt-6 flex items-end justify-between gap-10'>
                    <p
                        className='max-w-[36ch] text-[15px] leading-relaxed text-fz-sub'
                        style={rise(0.85)}
                    >
                        A studio of nine, working between {studio.cities.join(' and ')}. Six
                        projects a year, each seen through from the first reading to the last proof.
                    </p>

                    <div
                        className='hidden shrink-0 items-center gap-4 sm:flex'
                        style={rise(1)}
                        aria-hidden='true'
                    >
                        <span className={MICRO_FAINT}>Scroll</span>

                        <span className='relative block h-12 w-px overflow-hidden bg-fz-rule'>
                            <span className='fz-cue absolute inset-x-0 top-0 block h-4 bg-fz-rule-on' />
                        </span>
                    </div>
                </div>
            </div>
        </header>
    )
}
